import React from 'react';
import {
  IonItem,
  IonLabel,
  IonTextarea,
  IonButton,
} from '@ionic/react';
import { StrategyRecommendationOutput } from '../parameters-schema';
import { useTags } from '../../tags/tags-provider';
import LeafEditorModal from '../shared/leaf-editor-modal';
import ReorderableTagSection from '../shared/reorderable-tag-section';
import SectionHeader from '../shared/section-header';
import SectionContainer from '../shared/section-container';
import { useLeafEditor } from '../shared/use-leaf-editor';

interface StrategyRecommendationLeafEditorProps {
  output: StrategyRecommendationOutput;
  onUpdate: (newOutput: StrategyRecommendationOutput) => void;
  isOpen: boolean;
  onClose: () => void;
}

const StrategyRecommendationLeafEditor: React.FC<StrategyRecommendationLeafEditorProps> = ({
  output,
  onUpdate,
  isOpen,
  onClose
}) => {
  const { tags } = useTags();
  
  const {
    editingOutput,
    setEditingOutput,
    newTagName,
    setNewTagName,
    newTagDescription,
    setNewTagDescription,
    isAddingNewTag,
    addingProduct,
    handleSave,
    handleAddTag,
    handleCancelTagCreation,
    handleReorder,
    handleRemoveItem,
    handleSelectExisting,
    handleSelectProduct,
  } = useLeafEditor({
    output,
    onUpdate,
    isOpen,
    onClose
  });

  const productTags = tags?.filter(tag => tag.isProduct === true) || [];
  const nonProductTags = tags?.filter(tag => tag.isProduct === false) || [];

  const handleAddProductTag = async () => {
    const newTag = await handleAddTag(true);
    if (newTag) {
      setEditingOutput(prev => ({
        ...prev,
        strategyMenu: [...(prev.strategyMenu || []), newTag]
      }));
    }
  };

  const handleAddExtraTag = async () => {
    const newTag = await handleAddTag(false);
    if (newTag) {
      setEditingOutput(prev => ({
        ...prev,
        extraTags: [...(prev.extraTags || []), newTag]
      }));
    }
  };

  return (
    <LeafEditorModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Strategy Output"
    >
      {/* Basic Information Section */}
      <SectionHeader
        title="Basic Information"
        subtitle="Core details for this strategy recommendation"
        icon="information-circle"
      >
        <SectionContainer>
          <IonItem>
            <IonLabel position="stacked">Explanation</IonLabel>
            <IonTextarea
              value={editingOutput.explanation || ''}
              onIonInput={e => setEditingOutput(prev => ({
                ...prev,
                explanation: e.detail.value ?? ''
              }))}
              placeholder="Explain why this strategy is recommended..."
              rows={3}
            />
          </IonItem>
        </SectionContainer>
      </SectionHeader>

      {/* Strategy Menu Section */}
      <SectionHeader
        title="Strategy Menu"
        subtitle="Ordered list of recommended products (drag to reorder)"
        icon="list"
      >
        <SectionContainer>
          <ReorderableTagSection
            items={(editingOutput.strategyMenu || []).map(tagId => {
              const tag = productTags.find(t => t.id === tagId);
              return {
                id: tagId,
                displayName: tag?.displayName || 'Unknown Tag',
                description: tag?.description || 'No description'
              };
            })}
            onReorder={(from, to) => handleReorder('strategyMenu', from, to)}
            onRemove={(tagId) => handleRemoveItem('strategyMenu', tagId)}
            onSelectExisting={() => {}} // Not used for products
            onSelectProduct={(value) => handleSelectProduct('strategyMenu', value)}
            isProduct={true}
            label="Add Product to Strategy Menu"
            placeholder="Select a product or create new"
            emptyMessage="No products in strategy menu"
            isAddingNewTag={isAddingNewTag && addingProduct}
            tagName={newTagName}
            tagDescription={newTagDescription}
            onTagNameChange={setNewTagName}
            onTagDescriptionChange={setNewTagDescription}
            onAddTag={handleAddProductTag}
            onCancelTagCreation={handleCancelTagCreation}
            addingProduct={true}
          />
        </SectionContainer>
      </SectionHeader>

      {/* Extra Tags Section */}
      <SectionHeader
        title="Additional Tags"
        subtitle="Extra categorization tags for this recommendation"
        icon="pricetag"
      >
        <SectionContainer>
          <ReorderableTagSection
            items={(editingOutput.extraTags || []).map(tagId => {
              const tag = nonProductTags.find(t => t.id === tagId);
              return {
                id: tagId,
                displayName: tag?.displayName || 'Unknown Tag',
                description: tag?.description || 'No description'
              };
            })}
            onReorder={(from, to) => handleReorder('extraTags', from, to)}
            onRemove={(tagId) => handleRemoveItem('extraTags', tagId)}
            onSelectExisting={(value) => handleSelectExisting('extraTags', value)}
            isProduct={false}
            label="Add Extra Tag"
            placeholder="Select a tag or create new"
            emptyMessage="No extra tags"
            isAddingNewTag={isAddingNewTag && !addingProduct}
            tagName={newTagName}
            tagDescription={newTagDescription}
            onTagNameChange={setNewTagName}
            onTagDescriptionChange={setNewTagDescription}
            onAddTag={handleAddExtraTag}
            onCancelTagCreation={handleCancelTagCreation}
            addingProduct={false}
          />
        </SectionContainer>
      </SectionHeader>

      {/* Action Buttons */}
      <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
        <IonButton
          expand="block"
          fill="solid"
          onClick={handleSave}
        >
          Save Changes
        </IonButton>
        <IonButton
          expand="block"
          fill="clear"
          onClick={onClose}
        >
          Cancel
        </IonButton>
      </div>
    </LeafEditorModal>
  );
};

export default StrategyRecommendationLeafEditor;
